/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { ArrowDownwardRounded, ArrowUpwardRounded, DeleteRounded } from '@mui/icons-material';
import { Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { ManualDowntimePeriod } from '../../../shared/entities/manualDowntimePeriod';
import appTheme from '../../common/theme/appTheme.json';
export interface ManualDowntimePeriodsTableProps {
  manualDowntimePeriods: ManualDowntimePeriod[] | undefined;
  onManualDowntimePeriodDeleteIconClicked: (manualDowntimePeriod: ManualDowntimePeriod) => void;
}
const ManualDowntimePeriodsTable = ({
  manualDowntimePeriods,
  onManualDowntimePeriodDeleteIconClicked,
}: ManualDowntimePeriodsTableProps) => {
  // Local State
  const [sortField, setSortField] = useState<'startTimeEpochSecs' | 'endTimeEpochSecs' | 'creationTimeEpochSecs'>(
    'creationTimeEpochSecs',
  );
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  return (
    <Table sx={{ borderRadius: '8px', overflow: 'hidden' }}>
      <TableHead sx={{ backgroundColor: appTheme.primaryBackgroundColor }}>
        <TableRow>
          <TableCell sx={tableHeaderCellStyle}>
            <Button
              variant="text"
              sx={{
                fontSize: '16px',
                paddingLeft: 0,
                ...(sortField === 'startTimeEpochSecs'
                  ? { color: '#ffffff', fontWeight: 700 }
                  : { color: '#9FAFBC', fontWeight: 600 }),
              }}
              onClick={() => {
                if (sortField === 'startTimeEpochSecs') {
                  setSortDirection(sortDirection === 1 ? -1 : 1);
                } else {
                  setSortField('startTimeEpochSecs');
                }
              }}
              endIcon={
                sortField === 'startTimeEpochSecs' ? (
                  sortDirection === 1 ? (
                    <ArrowDownwardRounded sx={{ color: '#9FAFBC' }} />
                  ) : (
                    <ArrowUpwardRounded sx={{ color: '#9FAFBC' }} />
                  )
                ) : (
                  <></>
                )
              }
            >
              Downtime start
            </Button>
          </TableCell>
          <TableCell sx={tableHeaderCellStyle}>
            <Button
              variant="text"
              sx={{
                fontSize: '16px',
                paddingLeft: 0,
                ...(sortField === 'endTimeEpochSecs'
                  ? { color: '#ffffff', fontWeight: 700 }
                  : { color: '#9FAFBC', fontWeight: 600 }),
              }}
              onClick={() => {
                if (sortField === 'endTimeEpochSecs') {
                  setSortDirection(sortDirection === 1 ? -1 : 1);
                } else {
                  setSortField('endTimeEpochSecs');
                }
              }}
              endIcon={
                sortField === 'endTimeEpochSecs' ? (
                  sortDirection === 1 ? (
                    <ArrowDownwardRounded sx={{ color: '#9FAFBC' }} />
                  ) : (
                    <ArrowUpwardRounded sx={{ color: '#9FAFBC' }} />
                  )
                ) : (
                  <></>
                )
              }
            >
              Downtime end
            </Button>
          </TableCell>
          <TableCell sx={tableHeaderCellStyle}>
            <Button
              sx={{
                fontSize: '16px',
                paddingLeft: 0,
                ...(sortField === 'creationTimeEpochSecs'
                  ? { color: '#ffffff', fontWeight: 700 }
                  : { color: '#9FAFBC', fontWeight: 600 }),
              }}
              variant="text"
              onClick={() => {
                if (sortField === 'creationTimeEpochSecs') {
                  setSortDirection(sortDirection === 1 ? -1 : 1);
                } else {
                  setSortField('creationTimeEpochSecs');
                }
              }}
              endIcon={
                sortField === 'creationTimeEpochSecs' ? (
                  sortDirection === 1 ? (
                    <ArrowDownwardRounded sx={{ color: '#9FAFBC' }} />
                  ) : (
                    <ArrowUpwardRounded sx={{ color: '#9FAFBC' }} />
                  )
                ) : (
                  <></>
                )
              }
            >
              Created
            </Button>
          </TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(manualDowntimePeriods || [])
          .slice()
          .sort((a: ManualDowntimePeriod, b: ManualDowntimePeriod) =>
            a[sortField] < b[sortField] ? sortDirection * 1 : sortDirection * -1,
          )
          .map((manualDowntimePeriod: ManualDowntimePeriod) => (
            <TableRow key={manualDowntimePeriod.id}>
              <TableCell sx={tableBodyCellStyle}>
                {dayjs.unix(manualDowntimePeriod.startTimeEpochSecs).utc().format('DDMMMYYYY HH:mm[Z]').toUpperCase()}
              </TableCell>
              <TableCell sx={tableBodyCellStyle}>
                {dayjs.unix(manualDowntimePeriod.endTimeEpochSecs).utc().format('DDMMMYYYY HH:mm[Z]').toUpperCase()}
              </TableCell>
              <TableCell sx={{ ...tableBodyCellStyle, color: '#9FAFBC' }}>
                {dayjs
                  .unix(manualDowntimePeriod.creationTimeEpochSecs)
                  .utc()
                  .format('DDMMMYYYY HH:mm[Z]')
                  .toUpperCase()}
              </TableCell>
              <TableCell>
                <IconButton
                  onClick={() => {
                    onManualDowntimePeriodDeleteIconClicked(manualDowntimePeriod);
                  }}
                >
                  <DeleteRounded></DeleteRounded>
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
};
export default ManualDowntimePeriodsTable;

const tableHeaderCellStyle = {
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  letterSpacing: 0,
  color: '#9FAFBC',
};

const tableBodyCellStyle = {
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: 0,
};
